import { useCallback, useEffect, useState } from 'react';
import {
    authenticateWithPasskey,
    deletePasskey,
    getPasskeyApiError,
    getPasskeySupportMessage,
    isPasskeyCancelled,
    isPasskeySupported,
    isPlatformPasskeyAvailable,
    listPasskeys,
    peekPasskeyList,
    registerPasskey,
} from '../services/passkeyService';

export default function usePasskey() {
    const [supported, setSupported] = useState(false);
    const [platformAvailable, setPlatformAvailable] = useState(false);
    const [supportMessage, setSupportMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [passkeys, setPasskeys] = useState([]);

    useEffect(() => { 
        const ok = isPasskeySupported();
        setSupported(ok);
        setSupportMessage(getPasskeySupportMessage());
        let cancelled = false;
        isPlatformPasskeyAvailable().then((available) => {
            if (!cancelled) setPlatformAvailable(available);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const register = useCallback(async ({ userName, emailAddress, userId, token, deviceName } = {}) => {
        setBusy(true);
        setError('');
        try {
            const result = await registerPasskey({ userName, emailAddress, userId, token, deviceName });
            return result;
        } catch (err) {
            const message = getPasskeyApiError(err);
            if (!isPasskeyCancelled(err)) setError(message);
            throw err;
        } finally {
            setBusy(false);
        }
    }, []);

    const authenticate = useCallback(async (userName) => {
        setBusy(true);
        setError('');
        try {
            return await authenticateWithPasskey(userName);
        } catch (err) {
            const message = getPasskeyApiError(err);
            if (!isPasskeyCancelled(err)) setError(message);
            throw err;
        } finally {
            setBusy(false);
        }
    }, []);

    const refreshPasskeys = useCallback(async (token, userId, userName, { force = false } = {}) => {
        if (!token) {
            setPasskeys([]);
            return [];
        }
        const cached = force ? null : peekPasskeyList(userId, userName);
        if (cached) setPasskeys(cached);
        try {
            const rows = await listPasskeys({ token, userId, userName, emailAddress: userName, force });
            setPasskeys(rows);
            setError('');
            return rows;
        } catch (err) {
            if (!cached) setPasskeys([]);
            if (err?.response?.status !== 404) {
                setError(getPasskeyApiError(err, 'Could not load passkeys.'));
            }
            return cached || [];
        }
    }, []);

    const removePasskey = useCallback(async ({ token, credentialId, userId, userName } = {}) => {
        setBusy(true);
        setError('');
        try {
            const result = await deletePasskey({ token, credentialId, userId, userName, emailAddress: userName });
            await refreshPasskeys(token, userId, userName, { force: true });
            return result;
        } catch (err) {
            const message = getPasskeyApiError(err, 'Could not remove this passkey.');
            if (!isPasskeyCancelled(err)) setError(message);
            throw err;
        } finally {
            setBusy(false);
        }
    }, [refreshPasskeys]);

    return {
        supported,
        platformAvailable,
        supportMessage,
        busy,
        error,
        setError,
        passkeys,
        register,
        authenticate,
        refreshPasskeys,
        removePasskey,
    };
}
